import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

class UOYarnPurchaseSummary extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate, { signal });

    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });

    this.#update();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /** @param {Event & { detail?: { quantity?: number } }} event */
  #onQuantityUpdate = (event) => {
    this.#update(Number(event.detail?.quantity));
  };

  /** @param {Event & { promise?: Promise<any> }} event */
  #onProductSelect = (event) => {
    const sourceCard = event.target instanceof Element ? event.target.closest('product-card') : null;
    if (sourceCard && !sourceCard.contains(this)) return;
    if (!event.promise) return;

    event.promise
      .then(({ detail }) => {
        if (!detail) return;

        const productId = detail.newProduct?.id ?? detail.productId;
        if (!detail.newProduct && productId && String(productId) !== this.dataset.productId) return;

        if (detail.newProduct?.id) {
          this.dataset.productId = String(detail.newProduct.id);
        }

        const incomingSummaries = Array.from(detail.html?.querySelectorAll?.('uo-yarn-purchase-summary') ?? []);
        const incoming = incomingSummaries.find((summary) => summary.dataset.blockId === this.dataset.blockId);

        if (incoming) {
          for (const key of ['unitPrice', 'priceBreaks', 'moneyFormat', 'currency', 'productId']) {
            if (incoming.dataset[key] !== undefined) {
              this.dataset[key] = incoming.dataset[key];
            }
          }
        } else if (detail.resource?.price != null) {
          this.dataset.unitPrice = String(detail.resource.price);
        }

        requestAnimationFrame(() => this.#update());
      })
      .catch(() => {});
  };

  #onCartUpdate = (event) => {
    event.promise
      ?.then(() => requestAnimationFrame(() => this.#update()))
      .catch(() => {});
  };

  #getQuantityInput() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="quantity"]'));
  }

  #getQuantity() {
    const quantity = Number(this.#getQuantityInput()?.value);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  /** @param {number} quantity */
  #getUnitPrice(quantity) {
    const basePrice = Number(this.dataset.unitPrice);
    if (!Number.isFinite(basePrice)) return null;

    const cartQuantity = Number(this.#getQuantityInput()?.dataset.cartQuantity) || 0;
    const qualifyingQuantity = quantity + Math.max(cartQuantity, 0);

    let price = basePrice;
    try {
      const priceBreaks = JSON.parse(this.dataset.priceBreaks || '[]');
      if (Array.isArray(priceBreaks)) {
        priceBreaks
          .filter((priceBreak) => Number.isFinite(Number(priceBreak.quantity)) && Number.isFinite(Number(priceBreak.price)))
          .sort((a, b) => Number(a.quantity) - Number(b.quantity))
          .forEach((priceBreak) => {
            if (qualifyingQuantity >= Number(priceBreak.quantity)) {
              price = Number(priceBreak.price);
            }
          });
      }
    } catch {
      // Keep the base variant price if price-break data is unavailable.
    }

    return price;
  }

  /** @param {number} [quantity] */
  #update(quantity = this.#getQuantity()) {
    const safeQuantity = Number.isFinite(quantity) && quantity > 0 ? quantity : this.#getQuantity();
    const singular = this.dataset.unitSingular || 'item';
    const plural = this.dataset.unitPlural || `${singular}s`;
    const quantityText = this.querySelector('[data-uo-quantity-text]');

    if (quantityText) {
      quantityText.textContent = `${safeQuantity} ${safeQuantity === 1 ? singular : plural}`;
    }

    const total = this.querySelector('[data-uo-total]');
    const unitPrice = this.#getUnitPrice(safeQuantity);

    if (total && unitPrice != null) {
      total.innerHTML = formatMoney(
        unitPrice * safeQuantity,
        this.dataset.moneyFormat || '{{amount}}',
        this.dataset.currency || ''
      );
    }
  }
}

if (!customElements.get('uo-yarn-purchase-summary')) {
  customElements.define('uo-yarn-purchase-summary', UOYarnPurchaseSummary);
}
